import React from 'react'
import { Box, Typography } from '@mui/material'

interface FormattedDescriptionProps {
  description: string
}

export const FormattedDescription: React.FC<FormattedDescriptionProps> = ({ description }) => {
  if (!description) return null

  // Check if string contains HTML markup tags
  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(description)

  if (hasHtmlTags) {
    return (
      <Box
        sx={{
          py: 0.5,
          color: 'text.secondary',
          fontSize: '0.875rem',
          lineHeight: 1.6,
          '& h1, & h2, & h3, & h4': {
            color: 'text.primary',
            fontWeight: 800,
            mt: 1.5,
            mb: 0.5,
          },
          '& h1': { fontSize: '1.25rem' },
          '& h2': { fontSize: '1.1rem' },
          '& h3': { fontSize: '1rem', color: 'primary.main' },
          '& h4': { fontSize: '0.95rem' },
          '& p': { mb: 1 },
          '& ul, & ol': { pl: 2.5, mb: 1 },
          '& li': { mb: 0.25 },
          '& strong, & b': { color: 'text.primary', fontWeight: 700 },
          '& code': {
            bgcolor: 'action.hover',
            px: 0.8,
            py: 0.2,
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.8rem',
          },
        }}
        dangerouslySetInnerHTML={{ __html: description }}
      />
    )
  }

  // Parse Markdown inline formatting (**bold**, *italic*, _italic_, `code`)
  const renderInlineText = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*|\*.*?\*|_.*?_|`.*?`|<b.*?>.*?<\/b>|<strong.*?>.*?<\/strong>|<i.*?>.*?<\/i>)/g)
    return parts.map((part, index) => {
      if (!part) return null
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={index} style={{ fontWeight: 700, color: '#0F172A' }}>
            {part.slice(2, -2)}
          </strong>
        )
      }
      if ((part.startsWith('*') && part.endsWith('*')) || (part.startsWith('_') && part.endsWith('_'))) {
        return (
          <em key={index} style={{ fontStyle: 'italic' }}>
            {part.slice(1, -1)}
          </em>
        )
      }
      if (part.startsWith('`') && part.endsWith('`')) {
        return (
          <code key={index} style={{ backgroundColor: '#F1F5F9', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.8rem' }}>
            {part.slice(1, -1)}
          </code>
        )
      }
      return part
    })
  }

  const lines = description.split('\n')

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5 }}>
      {lines.map((line, idx) => {
        const trimmed = line.trim()
        if (!trimmed) return null

        // H1 Header: # Title
        if (trimmed.startsWith('# ')) {
          return (
            <Typography
              key={idx}
              variant="h6"
              sx={{ fontWeight: 900, color: 'text.primary', mt: 2, mb: 0.5, fontSize: '1.2rem' }}
            >
              {renderInlineText(trimmed.replace(/^#\s*/, ''))}
            </Typography>
          )
        }

        // H2 Header: ## Title
        if (trimmed.startsWith('##')) {
          return (
            <Typography
              key={idx}
              variant="subtitle1"
              sx={{ fontWeight: 800, color: 'text.primary', mt: 1.75, mb: 0.5, fontSize: '1.05rem' }}
            >
              {renderInlineText(trimmed.replace(/^##\s*/, ''))}
            </Typography>
          )
        }

        // H3/H4 Header: ### Title or #### Title
        if (trimmed.startsWith('###')) {
          return (
            <Typography
              key={idx}
              variant="subtitle2"
              sx={{ fontWeight: 800, color: 'primary.main', mt: 1.5, mb: 0.5, fontSize: '0.95rem' }}
            >
              {renderInlineText(trimmed.replace(/^#{3,4}\s*/, ''))}
            </Typography>
          )
        }

        // Bullet point: - item or * item
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
          const content = trimmed.replace(/^[-*]\s*/, '')
          return (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, ml: 1, my: 0.25 }}>
              <Typography component="span" sx={{ color: 'primary.main', fontWeight: 800, fontSize: '1rem', lineHeight: 1.4 }}>
                •
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.875rem' }}>
                {renderInlineText(content)}
              </Typography>
            </Box>
          )
        }

        // Numbered list item: 1. item
        if (/^\d+\.\s/.test(trimmed)) {
          const num = trimmed.match(/^(\d+\.)\s/)?.[1]
          const content = trimmed.replace(/^\d+\.\s*/, '')
          return (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, ml: 1, my: 0.25 }}>
              <Typography component="span" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.875rem', lineHeight: 1.6 }}>
                {num}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.875rem' }}>
                {renderInlineText(content)}
              </Typography>
            </Box>
          )
        }

        // Standard paragraph
        return (
          <Typography key={idx} variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.875rem', mb: 0.5 }}>
            {renderInlineText(trimmed)}
          </Typography>
        )
      })}
    </Box>
  )
}
export default FormattedDescription
